import { Dispatch, SetStateAction } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ISaveEmployer } from "../../interfaces/services";
import { EtyEmployer } from "../../interfaces/entities";
import { ISelectedData } from "../../interfaces/tree";

import useToast from "../useToast";

import { saveEmployer } from "../../services/employers";

export interface IPayload {
    payload: ISaveEmployer;
    setIsProcessing: Dispatch<SetStateAction<boolean>>;
    selected: ISelectedData | null;
    setSelected: Dispatch<SetStateAction<ISelectedData | null>>;
};

export interface IResponse {
    error?: string;
    message: string;
    statusCode: number;
    data: EtyEmployer;
};

const useSaveEmployer = () => {
    const queryClient = useQueryClient();

    const { addToast } = useToast();

    const mutationFn = async (data: IPayload): Promise<IResponse> => {
        return await saveEmployer(data.payload);
    };

    return useMutation({
        mutationFn,
        onSuccess: (response: IResponse, variables: IPayload) => {
            if (response?.statusCode === 200) {
                addToast({
                    type: 'success',
                    title: 'Employer saved successfully.',
                });

                if (variables.selected) {
                    variables.setSelected({
                        id: response.data.id,
                        level: 1,
                        data: {
                            ...(variables.selected.data as EtyEmployer),
                            id: response.data.id,
                            organization_id: response.data.organization_id,
                            type: response.data.type,
                            operating_name: response.data.operating_name,
                            legal_name: response.data.legal_name,
                            naics: response.data.naics,
                            cra_number: response.data.cra_number,
                            tech_company: response.data.tech_company,
                        }
                    });
                };

                queryClient.invalidateQueries({ queryKey: ['organizations_tree'] });
            };
        },
        onError: (error: IResponse, variables: IPayload) => {
            addToast({
                type: 'error',
                title: 'Error occured',
                message: error.message
            });
        },
        onSettled: (data: IResponse | undefined, error: IResponse | null, variables: IPayload) => {
            variables.setIsProcessing(false);
        },
    });
};

export default useSaveEmployer;